import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { YEAR_LEVEL_LABELS } from '@qq/shared';
import { ApiRequestError, api } from '../lib/api.js';

interface ChildForm {
  nickname: string;
  dob: string;
  yearLevelLabel: string;
  pin: string;
  interests: string;
}

const emptyChild = (): ChildForm => ({
  nickname: '',
  dob: '',
  yearLevelLabel: 'Prep',
  pin: '',
  interests: '',
});

const DRAFT_KEY = 'qq.setup.draft.v1';

interface Draft {
  familyName: string;
  parentName: string;
  email: string;
  children: Array<Omit<ChildForm, 'pin'>>;
}

/**
 * Keep what has been typed across a refresh.
 *
 * Dates of birth and names are tedious to re-enter and losing them to an
 * accidental reload is infuriating. The password, its confirmation, the PINs
 * and the setup code are deliberately NOT kept: they are credentials, this is
 * a shared family browser, and a draft that is never submitted would leave
 * them sitting in storage indefinitely. Those four are short to retype.
 */
function readDraft(): Draft | null {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    // Private browsing, or site data blocked. Not being able to restore a
    // draft must never stop somebody setting the app up.
    return null;
  }
}

function clearDraft(): void {
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * First-run setup, in the browser.
 *
 * This exists because the alternative was an SSH command, and a setup step
 * that is awkward to perform is one that gets performed badly - or with a
 * weak password chosen to make the command shorter to type.
 *
 * It is only reachable while the app has no parent account. Once it does, the
 * server refuses this endpoint permanently, so it can never become a public
 * sign-up form.
 */
export function FirstRunSetup({ onDone }: { onDone: () => void }) {
  const [draft] = useState(readDraft);
  const [token, setToken] = useState('');
  const [familyName, setFamilyName] = useState(draft?.familyName ?? 'Our Family');
  const [parentName, setParentName] = useState(draft?.parentName ?? '');
  const [email, setEmail] = useState(draft?.email ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [children, setChildren] = useState<ChildForm[]>(
    draft?.children?.length
      ? draft.children.map((c) => ({ ...emptyChild(), ...c, pin: '' }))
      : [emptyChild()],
  );

  // Saved on every keystroke rather than on unload: a tab closed by the OS,
  // or an iPad going to sleep, never fires the tidy events.
  useEffect(() => {
    try {
      const toSave: Draft = {
        familyName,
        parentName,
        email,
        children: children.map(({ pin: _pin, ...rest }) => rest),
      };
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(toSave));
    } catch {
      /* storage unavailable; the form still works, it just will not survive */
    }
  }, [familyName, parentName, email, children]);

  const setup = useMutation({
    mutationFn: () =>
      api('/setup', {
        method: 'POST',
        body: {
          token: token.trim(),
          familyName: familyName.trim(),
          parentName: parentName.trim(),
          email: email.trim(),
          password,
          children: children.map((c) => ({
            nickname: c.nickname.trim(),
            dob: c.dob,
            yearLevelLabel: c.yearLevelLabel,
            pin: c.pin.trim(),
            interests: c.interests
              .split(',')
              .map((t) => t.trim().toLowerCase())
              .filter((t) => t.length >= 2),
          })),
        },
      }),
    onSuccess: () => {
      clearDraft();
      onDone();
    },
  });

  const update = (index: number, patch: Partial<ChildForm>) =>
    setChildren(children.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  const passwordsMatch = password.length === 0 || password === confirm;

  // A disabled button that will not say why is a dead end. Every reason the
  // form is not ready is listed next to it, in the words used on the labels.
  const missing: string[] = [];
  if (token.trim().length === 0) missing.push('the setup code from the server');
  if (parentName.trim().length === 0) missing.push('your name');
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) missing.push('a valid email address');
  if (password.length < 10) missing.push('a password of at least 10 characters');
  else if (password !== confirm) missing.push('the password typed the same way twice');
  children.forEach((c, i) => {
    const who = c.nickname.trim() || `child ${i + 1}`;
    if (c.nickname.trim().length === 0) missing.push(`a name for child ${i + 1}`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(c.dob)) missing.push(`a date of birth for ${who}`);
    if (!/^\d{4}$/.test(c.pin)) missing.push(`a 4-digit PIN for ${who}`);
  });
  const ready = missing.length === 0;

  const problem = setup.error instanceof ApiRequestError ? setup.error.body.message : null;

  return (
    <main className="screen setup">
      <h1>Let&rsquo;s set this up</h1>
      <p>This screen only works once. After today it disappears for good.</p>
      <p className="muted">
        Names, dates and interests are kept if you refresh this page. The setup code, the password
        and the PINs are not &mdash; those are secrets, and this is a shared browser.
      </p>

      <section className="card card--soft">
        <h2>Setup code</h2>
        <p className="muted">
          From the server, so nobody who simply finds this address can claim your family. Run this
          on your machine and paste the value:
        </p>
        <pre className="code-line">
          ssh quokka &quot;grep SETUP_TOKEN /opt/quokka-quest/.env&quot;
        </pre>
        <label>
          Setup code
          <input value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" />
        </label>
      </section>

      <section className="card card--soft">
        <h2>You</h2>
        <div className="field-row">
          <label>
            Your name
            <input value={parentName} onChange={(e) => setParentName(e.target.value)} />
          </label>
          <label>
            Family name
            <input value={familyName} onChange={(e) => setFamilyName(e.target.value)} />
          </label>
          <label>
            Email (this is your sign-in)
            <input
              type="email"
              value={email}
              autoComplete="username"
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
        </div>
        <div className="field-row">
          <label>
            Password (10 characters or more)
            <input
              type="password"
              value={password}
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label>
            Type it again
            <input
              type="password"
              value={confirm}
              autoComplete="new-password"
              onChange={(e) => setConfirm(e.target.value)}
            />
          </label>
        </div>
        {!passwordsMatch && <p className="error-note">Those two do not match yet.</p>}
        <p className="muted">
          Choose the one you want — there is no change-password screen yet, so changing it later
          means another trip to the server.
        </p>
      </section>

      {children.map((child, index) => (
        <section key={index} className="card card--soft">
          <h2>Child {index + 1}</h2>
          <div className="field-row">
            <label>
              What they are called
              <input
                value={child.nickname}
                placeholder="Archie"
                onChange={(e) => update(index, { nickname: e.target.value })}
              />
            </label>
            <label>
              Date of birth
              <input
                type="date"
                value={child.dob}
                onChange={(e) => update(index, { dob: e.target.value })}
              />
            </label>
            <label>
              Year level
              <select
                value={child.yearLevelLabel}
                onChange={(e) => update(index, { yearLevelLabel: e.target.value })}
              >
                {YEAR_LEVEL_LABELS.map((y) => (
                  <option key={y}>{y}</option>
                ))}
              </select>
            </label>
            <label>
              Their PIN (4 digits)
              <input
                value={child.pin}
                inputMode="numeric"
                maxLength={4}
                placeholder="1234"
                onChange={(e) => update(index, { pin: e.target.value.replace(/\D/g, '') })}
              />
            </label>
          </div>
          <label>
            What they like, separated by commas
            <input
              value={child.interests}
              placeholder="dinosaurs, animals, footy"
              onChange={(e) => update(index, { interests: e.target.value })}
            />
          </label>
          <p className="muted">
            Interests theme their activities — a counting question becomes dinosaurs rather than
            dots. You can change all of this later in admin.
          </p>
          {children.length > 1 && (
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => setChildren(children.filter((_, i) => i !== index))}
            >
              Remove this one
            </button>
          )}
        </section>
      ))}

      {children.length < 4 && (
        <button
          type="button"
          className="btn btn--outline"
          onClick={() => setChildren([...children, emptyChild()])}
        >
          Add another child
        </button>
      )}

      {problem && <p className="error-note">{problem}</p>}

      {!ready && (
        <div className="error-note">
          <p>Still needed before this can be created:</p>
          <ul className="missing-list">
            {missing.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        className="btn btn--big"
        disabled={!ready || setup.isPending}
        onClick={() => setup.mutate()}
      >
        {setup.isPending ? 'Setting everything up…' : 'Create our family'}
      </button>

      <p className="muted">
        This also loads the whole curriculum — 62 skills across English and Maths — so there is
        nothing else to install afterwards.
      </p>
    </main>
  );
}
